import qs from "qs";
import {FilterDef, HTTPGenericResponse} from "../../constants/FinanceModels";
import {getFilterValue} from "../../utils/FilterUtils";
import {useApi} from "../Api";
import {
    ID_FILTER_AMOUNT_FROM,
    ID_FILTER_AMOUNT_TO,
    ID_FILTER_CORRELATION_ID,
    ID_FILTER_CORRELATION_NAME,
    ID_FILTER_CREATION_DATE_FROM,
    ID_FILTER_CREATION_DATE_TO,
    ID_FILTER_CUSTOMER_COMPANY_NAME,
    ID_FILTER_CUSTOMER_FIRST_NAME,
    ID_FILTER_CUSTOMER_ID,
    ID_FILTER_CUSTOMER_LAST_NAME,
    ID_FILTER_DEALERS,
    ID_FILTER_DUE_DATE_FROM,
    ID_FILTER_DUE_DATE_TO,
    ID_FILTER_INVOICE_NUMBER,
    ID_FILTER_INVOICEDATEFROM,
    ID_FILTER_INVOICEDATETO,
    ID_FILTER_INVOICETYPE,
    ID_FILTER_LEGAL_ENTITIES,
    ID_FILTER_PARENT_SAP_INVOICE_NUMBER,
    ID_FILTER_PAYMENT_METHOD,
    ID_FILTER_RA_CODE,
    ID_FILTER_SAP_INVOICE_NUMBER,
    ID_FILTER_SEARCH,
    ID_FILTER_STATUS,
    ID_FILTER_TERMS_OF_PAYMENT
} from "../../pages/invoices/constants/InvoicesFilters";
import dayjs from "dayjs";
import {createContext, useContext} from "react";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {
    ID_FILTER_AMOUNT_TYPE,
    ID_FILTER_COMPANY_NAME,
    ID_FILTER_CREATIONDATEFROM,
    ID_FILTER_CREATIONDATETO,
    ID_FILTER_CUSTOMER_LASTNAME,
    ID_FILTER_CUSTOMER_NAME,
    ID_FILTER_INVOICE_DATE_FROM,
    ID_FILTER_INVOICE_DATE_TO,
    ID_FILTER_LASTTRANSACTIONDATEFROM,
    ID_FILTER_LASTTRANSACTIONDATETO,
    ID_FILTER_MODIFICATIONDATEFROM,
    ID_FILTER_MODIFICATIONDATETO,
    ID_FILTER_PAN_CARD,
    ID_FILTER_PAYMENT_MODE,
    ID_FILTER_PAYMENT_TYPE,
    ID_FILTER_STATUSES,
    ID_PREAUTH_IS_RELEASED
} from "../../pages/payments/pages/components/filters/PaymentRequestConstants";
import carsharingService, {CarsharingRedirectParams, CarsharingSection} from "../../../../util/carsharingService";
import {CarSharingContextType} from "../../../customerbase/component/carSharing/CarSharingContext";

export const useCarSharingRepository = () => {
    const {GET} = useApi();

    const PATH = "/carsharing"


    const DATE_FIELDS = [
        ID_FILTER_DUE_DATE_FROM,
        ID_FILTER_DUE_DATE_TO,
        ID_FILTER_INVOICE_DATE_FROM,
        ID_FILTER_INVOICE_DATE_TO,
        ID_FILTER_CREATIONDATEFROM,
        ID_FILTER_CREATIONDATETO,
        ID_FILTER_MODIFICATIONDATEFROM,
        ID_FILTER_MODIFICATIONDATETO,
        ID_FILTER_LASTTRANSACTIONDATEFROM,
        ID_FILTER_LASTTRANSACTIONDATETO,
    ];
    const OTHER_FIELDS = [
        ID_FILTER_AMOUNT_TYPE,
        ID_FILTER_CUSTOMER_ID,
        ID_FILTER_PAYMENT_TYPE,
        ID_FILTER_SAP_INVOICE_NUMBER,
        ID_FILTER_PAYMENT_MODE,
        ID_FILTER_STATUSES,
        ID_FILTER_PAN_CARD,
        ID_PREAUTH_IS_RELEASED,
        ID_FILTER_CUSTOMER_NAME,
        ID_FILTER_CUSTOMER_LASTNAME,
        ID_FILTER_COMPANY_NAME,
        ID_FILTER_LEGAL_ENTITIES,
        ID_FILTER_DEALERS,
    ];

    const outStandingDefaultValues = [
        "OUTSTANDING",
        "SUSPENDED",
        "EXTERNAL_MANAGEMENT",
        "ACCOUNTED",
    ];

    const {globalData: {user}} = useContext(GlobalContext);
    const allLegalEntities: any[] = user.legalEntities?.filter((x: any) => x.name.toLowerCase() !== "all").map((x: any) => x.name);

    //-------------------- Invoices related functions --------------------//

    function getInvoicesCs(offset: number, limit: number, sort: string | null, order: string | null, filterDefs: FilterDef[] = [], customerUrl: string | null): Promise<HTTPGenericResponse> {
        let search = getFilterValue(ID_FILTER_SEARCH, filterDefs);
        let customerFirstName = getFilterValue(ID_FILTER_CUSTOMER_FIRST_NAME, filterDefs);
        let customerLastName = getFilterValue(ID_FILTER_CUSTOMER_LAST_NAME, filterDefs);
        let customerId = customerUrl ? customerUrl : getFilterValue(ID_FILTER_CUSTOMER_ID, filterDefs);
        let customerCompanyName = getFilterValue(ID_FILTER_CUSTOMER_COMPANY_NAME, filterDefs);
        let types = getFilterValue(ID_FILTER_INVOICETYPE, filterDefs);
        let statuses = getFilterValue(ID_FILTER_STATUS, filterDefs);
        let dateFrom: Date = getFilterValue(ID_FILTER_INVOICEDATEFROM, filterDefs);
        let dateTo: Date = getFilterValue(ID_FILTER_INVOICEDATETO, filterDefs);
        let invoiceNumber = getFilterValue(ID_FILTER_INVOICE_NUMBER, filterDefs);
        let sapInvoiceNumber = getFilterValue(ID_FILTER_SAP_INVOICE_NUMBER, filterDefs);
        let parentSapInvoiceNumber = getFilterValue(ID_FILTER_PARENT_SAP_INVOICE_NUMBER, filterDefs);
        let raCode = getFilterValue(ID_FILTER_RA_CODE, filterDefs);
        let correlationName = getFilterValue(ID_FILTER_CORRELATION_NAME, filterDefs);
        let correlationId = getFilterValue(ID_FILTER_CORRELATION_ID, filterDefs);

        let dueDateFrom: Date = getFilterValue(ID_FILTER_DUE_DATE_FROM, filterDefs);
        let dueDateTo: Date = getFilterValue(ID_FILTER_DUE_DATE_TO, filterDefs);
        let creationDateFrom: Date = getFilterValue(ID_FILTER_CREATION_DATE_FROM, filterDefs);
        let creationDateTo: Date = getFilterValue(ID_FILTER_CREATION_DATE_TO, filterDefs);
        let paymentMethod = getFilterValue(ID_FILTER_PAYMENT_METHOD, filterDefs);
        let termsOfPayment = getFilterValue(ID_FILTER_TERMS_OF_PAYMENT, filterDefs);
        let amountFrom: number = getFilterValue(ID_FILTER_AMOUNT_FROM, filterDefs);
        let amountTo: number = getFilterValue(ID_FILTER_AMOUNT_TO, filterDefs);
        let legalEntities: string[] = getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) || allLegalEntities;
        let dealers: string[] = getFilterValue(ID_FILTER_DEALERS, filterDefs);

        let params = {
            offset: offset,
            limit: limit,
            sort: sort,
            order: order,
            search: search,
            customerFirstName: customerFirstName,
            customerLastName: customerLastName,
            customerId: customerId,
            customerCompanyName: customerCompanyName,
            types: types,
            statuses: statuses,
            invoiceNumber: invoiceNumber,
            sapInvoiceNumber: sapInvoiceNumber,
            parentSapInvoiceNumber: parentSapInvoiceNumber,
            raCode: raCode,
            from: dateFrom ? dayjs(dateFrom).toISOString() : null,
            to: dateTo ? dayjs(dateTo).toISOString() : null,
            accountType: "RECEIVABLE",
            correlationName: correlationName,
            correlationId: correlationId,
            dueDateFrom: dueDateFrom,
            dueDateTo: dueDateTo,
            creationDateFrom: creationDateFrom,
            creationDateTo: creationDateTo,
            paymentMethod: paymentMethod,
            termsOfPayment: termsOfPayment,
            amountFrom: amountFrom ? amountFrom * 100 : amountFrom,
            amountTo: amountTo ? amountTo * 100 : amountTo,
            legalEntities,
            dealers
        }

        return GET(`${PATH}/invoice/all`, {
            params: params,
            paramsSerializer: (params) => qs.stringify(params, {arrayFormat: 'repeat', skipNulls: true})
        });
    }

    function getSummaryCs(filterDefs: FilterDef[] = []): Promise<HTTPGenericResponse> {
        let search = getFilterValue(ID_FILTER_SEARCH, filterDefs);
        let customerFirstName = getFilterValue(ID_FILTER_CUSTOMER_FIRST_NAME, filterDefs);
        let customerLastName = getFilterValue(ID_FILTER_CUSTOMER_LAST_NAME, filterDefs);
        let customerId = getFilterValue(ID_FILTER_CUSTOMER_ID, filterDefs);
        let customerCompanyName = getFilterValue(ID_FILTER_CUSTOMER_COMPANY_NAME, filterDefs);
        let types = getFilterValue(ID_FILTER_INVOICETYPE, filterDefs);
        let statuses = getFilterValue(ID_FILTER_STATUS, filterDefs);
        let dateFrom: Date = getFilterValue(ID_FILTER_INVOICEDATEFROM, filterDefs);
        let dateTo: Date = getFilterValue(ID_FILTER_INVOICEDATETO, filterDefs);
        let invoiceNumber = getFilterValue(ID_FILTER_INVOICE_NUMBER, filterDefs);
        let sapInvoiceNumber = getFilterValue(ID_FILTER_SAP_INVOICE_NUMBER, filterDefs);
        let parentSapInvoiceNumber = getFilterValue(ID_FILTER_PARENT_SAP_INVOICE_NUMBER, filterDefs);
        let raCode = getFilterValue(ID_FILTER_RA_CODE, filterDefs);
        let correlationName = getFilterValue(ID_FILTER_CORRELATION_NAME, filterDefs);
        let correlationId = getFilterValue(ID_FILTER_CORRELATION_ID, filterDefs);

        let dueDateFrom: Date = getFilterValue(ID_FILTER_DUE_DATE_FROM, filterDefs);
        let dueDateTo: Date = getFilterValue(ID_FILTER_DUE_DATE_TO, filterDefs);
        let creationDateFrom: Date = getFilterValue(ID_FILTER_CREATION_DATE_FROM, filterDefs);
        let creationDateTo: Date = getFilterValue(ID_FILTER_CREATION_DATE_TO, filterDefs);
        let paymentMethod = getFilterValue(ID_FILTER_PAYMENT_METHOD, filterDefs);
        let termsOfPayment = getFilterValue(ID_FILTER_TERMS_OF_PAYMENT, filterDefs);
        let amountFrom: number = getFilterValue(ID_FILTER_AMOUNT_FROM, filterDefs);
        let amountTo: number = getFilterValue(ID_FILTER_AMOUNT_TO, filterDefs);
        let legalEntities: string[] = getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) || allLegalEntities;
        let dealers: string[] = getFilterValue(ID_FILTER_DEALERS, filterDefs);

        let params = {
            search: search,
            customerFirstName: customerFirstName,
            customerLastName: customerLastName,
            customerId: customerId,
            customerCompanyName: customerCompanyName,
            invoiceNumber: invoiceNumber,
            sapInvoiceNumber: sapInvoiceNumber,
            parentSapInvoiceNumber: parentSapInvoiceNumber,
            raCode: raCode,
            types: types,
            statuses: statuses,
            from: dateFrom ? dayjs(dateFrom).toISOString() : null,
            to: dateTo ? dayjs(dateTo).toISOString() : null,
            accountType: "RECEIVABLE",
            correlationName: correlationName,
            correlationId: correlationId,
            dueDateFrom: dueDateFrom,
            dueDateTo: dueDateTo,
            creationDateFrom: creationDateFrom,
            creationDateTo: creationDateTo,
            paymentMethod: paymentMethod,
            termsOfPayment: termsOfPayment,
            amountFrom: amountFrom ? amountFrom * 100 : amountFrom,
            amountTo: amountTo ? amountTo * 100 : amountTo,
            legalEntities,
            dealers
        }

        return GET(`${PATH}/invoice/all-types-summary`, {
            params,
            paramsSerializer: (params) => qs.stringify(params, {arrayFormat: 'repeat', skipNulls: true})
        });
    }

    function downloadPDFCs(id: string) {
        return GET(`${PATH}/invoice/pdf/${id}`);
    }

    function downloadXMLCs(id: string) {
        return GET(`${PATH}/invoice/xml/${id}`);
    }

    //-------------------- Payment Requests related functions --------------------//
    function getPaymentsRequestsCs(
        offset: number,
        limit: number,
        sort: string | null,
        order: string | null,
        filterDefs: FilterDef[] = [],
        customer: string | null,
        invoiceNr: string | null,
        correlationName: string | null,
        correlationId: string | null,
        correlationIdStrict: boolean | null,
        outstanding: boolean | null
    ): Promise<HTTPGenericResponse> {
        let type = getFilterValue("paymentType", filterDefs);
        let amountType = getFilterValue("amountType", filterDefs);
        let customerId = customer
            ? customer
            : getFilterValue("customerId", filterDefs);
        let sapInvoiceNumber = getFilterValue("sapInvoiceNumber", filterDefs);
        let statuses = getFilterValue("statuses", filterDefs);
        let invoiceDateFrom: Date = getFilterValue("invoiceDateFrom", filterDefs);
        let invoiceDateTo: Date = getFilterValue("invoiceDateTo", filterDefs);
        let fromDueDate: Date = getFilterValue("dueDateFrom", filterDefs);
        let toDueDate: Date = getFilterValue("dueDateTo", filterDefs);
        let fromAmount: number = getFilterValue("amountFrom", filterDefs);
        let toAmount: number = getFilterValue("amountTo", filterDefs);
        let paymentMode = getFilterValue("paymentMode", filterDefs);
        let panCard = getFilterValue("maskeredPan", filterDefs);
        let fromCreationDate: Date = getFilterValue("fromCreationDate", filterDefs);
        let toCreationDate: Date = getFilterValue("toCreationDate",filterDefs);
        let fromModificationDate: Date = getFilterValue("fromModificationDate", filterDefs);
        let toModificationDate: Date = getFilterValue("toModificationDate",filterDefs);
        let preauthIsReleased = getFilterValue("preauthIsReleased", filterDefs);
        let customerName = getFilterValue("customerName", filterDefs);
        let customerLastName = getFilterValue("customerLastName",filterDefs);
        let companyName = getFilterValue("companyName", filterDefs);
        let alreadySentToSap = outstanding ? false : null;
        let fromTransactionDate: Date = getFilterValue("fromTransactionDate", filterDefs);
        let toTransactionDate: Date = getFilterValue("toTransactionDate",filterDefs);
        let correlationNameParameter = correlationName
            ? correlationName
            : getFilterValue("correlationName", filterDefs);
        let correlationIdParameter = correlationId
            ? correlationId
            : getFilterValue("correlationId", filterDefs);

        let invoiceNumber = invoiceNr
            ? invoiceNr
            : getFilterValue("invoiceNumber", filterDefs);
        let releasedAmountFrom: number = getFilterValue("releasedAmountFrom",filterDefs);
        let releasedAmountTo: number = getFilterValue("releasedAmountTo",filterDefs);
        let retryMade = getFilterValue("retryMade", filterDefs);
        let legalEntities: string[] =
            getFilterValue("legalEntities", filterDefs) || allLegalEntities;
        let dealers: string[] = getFilterValue("dealers", filterDefs);


        let params= {
            offset: offset,
            limit: limit,
            sortBy: sort,
            order: order,
            amountType: amountType,
            fromAmount: fromAmount ? fromAmount * 100 : fromAmount,
            toAmount: toAmount ? toAmount * 100 : toAmount,
            type: type,
            status: statuses,
            maskeredPan: panCard,
            fromInvoiceDate: invoiceDateFrom
                ? dayjs(invoiceDateFrom).toISOString()
                : null,
            toInvoiceDate: invoiceDateTo ? dayjs(invoiceDateTo).toISOString() : null,
            fromDueDate: fromDueDate ? dayjs(fromDueDate).toISOString() : null,
            toDueDate: toDueDate ? dayjs(toDueDate).toISOString() : null,
            fromCreatedDate: fromCreationDate
                ? dayjs(fromCreationDate).toISOString()
                : null,
            toCreatedDate: toCreationDate
                ? dayjs(toCreationDate).toISOString()
                : null,
            fromModifiedDate: fromModificationDate
                ? dayjs(fromModificationDate).toISOString()
                : null,
            toModifiedDate: toModificationDate
                ? dayjs(toModificationDate).toISOString()
                : null,
            sapInvoiceNumber: sapInvoiceNumber,
            customerId: customerId,
            cardTerminalType: paymentMode,
            preauthIsReleased: preauthIsReleased ? preauthIsReleased : null,
            customerName: customerName,
            customerLastName: customerLastName,
            customerRagioneSociale: companyName,
            alreadySentToSap: alreadySentToSap,
            correlationName: correlationNameParameter,
            correlationId: correlationIdParameter,
            isOutstanding: outstanding ? true : null,
            fromTransactionDate: fromTransactionDate
                ? dayjs(fromTransactionDate).toISOString()
                : null,
            toTransactionDate: toTransactionDate
                ? dayjs(toTransactionDate).toISOString()
                : null,
            invoiceNumber: invoiceNumber,
            releasedAmountFrom: releasedAmountFrom
                ? releasedAmountFrom * 100
                : releasedAmountFrom,
            releasedAmountTo: releasedAmountTo
                ? releasedAmountTo * 100
                : releasedAmountTo,
            retryMade: retryMade,
            correlationIdStrict: correlationIdParameter ? correlationIdStrict : null,
            dealers,
            legalEntities,
        };

       return GET(`${PATH}/payment`, {
            params: params,
            paramsSerializer: (params) =>
                qs.stringify(params, {arrayFormat: "repeat", skipNulls: true}),
        });
    }

    function getSummaryRequestCs(
        filterDefs: FilterDef[] = [],
        outstanding: boolean | null
    ): Promise<HTTPGenericResponse> {
        let params: any = {};
        let legalEntities: string[] =
            getFilterValue("legalEntities", filterDefs) || allLegalEntities;
        let dealers: string[] = getFilterValue("dealers", filterDefs);

        DATE_FIELDS.forEach((p) => {
            let date: Date = getFilterValue(p, filterDefs);
            params[p] = date ? dayjs(date).toISOString() : null;
        });
        OTHER_FIELDS.forEach((p) => {
            params[p] = getFilterValue(p, filterDefs);
        });
        params["correlationName"] = getFilterValue(
            "correlationName",
            filterDefs
        );
        params["correlationId"] = getFilterValue(
            "correlationId",
            filterDefs
        );
        params["type"] = getFilterValue("paymentType", filterDefs);
        params["fromAmount"] = getFilterValue("amountFrom", filterDefs);
        params["toAmount"] = getFilterValue("amountTo", filterDefs);
        params["fromAmount"] = params["fromAmount"]
            ? params["fromAmount"] * 100
            : params["fromAmount"];
        params["toAmount"] = params["toAmount"]
            ? params["toAmount"] * 100
            : params["toAmount"];
        params["cardTerminalType"] = getFilterValue(
            "paymentMode",
            filterDefs
        );
        params["isOutstanding"] = outstanding ? true : null;
        params["alreadySentToSap"] = outstanding ? false : null;
        params["invoiceNumber"] = getFilterValue(
            "invoiceNumber",
            filterDefs
        );
        params["releasedAmountFrom"] = params["releasedAmountFrom"]
            ? params["releasedAmountFrom"] * 100
            : params["releasedAmountFrom"];
        params["releasedAmountTo"] = params["releasedAmountTo"]
            ? params["releasedAmountTo"] * 100
            : params["releasedAmountTo"];
        params["retryMade"] = getFilterValue("retryMade", filterDefs);
        params["legalEntities"] = legalEntities;
        params["dealers"] = dealers;

        return GET(`${PATH}/payment/statuses`, {
            params: params,
            paramsSerializer: (params) =>
                qs.stringify(params, {arrayFormat: "repeat", skipNulls: true}),
        });
    }

    const CarSharingContext = createContext<CarSharingContextType>({});
    const useCarSharingContext = () => useContext(CarSharingContext);
    const {  language, market } = useCarSharingContext();
    const handleRedirect = async (customerId: string, section: CarsharingSection) => {
        if (!customerId) return;

        const params: CarsharingRedirectParams = {
            lang: language || 'it',
            type: 'CUSTOMER',
            section: section,
            id: String(customerId)
        };

        try {
            await redirectToCarSharing(params);
        } catch (error) {
            console.error('Redirect failed:', error);
        }
    };

    const redirectToCarSharing = async (params: any): Promise<void> => {
        return carsharingService.redirectToCarsharing(params);
    };

    return {getInvoicesCs, getSummaryCs, downloadPDFCs, handleRedirect, redirectToCarSharing, getPaymentsRequestsCs, getSummaryRequestCs};
}